(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.CartDrawerRecommendations = api;
  }
})(typeof window !== 'undefined' ? window : undefined, function () {
  const parseProductIds = (value) => {
    const ids = Array.isArray(value) ? value : String(value || '').split(',');
    return ids.map((id) => String(id).trim()).filter(Boolean);
  };

  const buildRecommendationsUrl = (baseUrl, productId, intent, limit) => {
    const path = String(baseUrl || '/recommendations/products').replace(/\.json$/, '');
    const params = new URLSearchParams({
      product_id: String(productId),
      intent: String(intent),
      limit: String(limit),
    });
    return `${path}.json?${params.toString()}`;
  };

  const uniqueEligibleProducts = (groups, cartProductIds, limit) => {
    const excluded = new Set(parseProductIds(cartProductIds));
    const selected = [];
    const seen = new Set();

    for (const group of groups || []) {
      for (const product of group || []) {
        const productId = String(product?.id || '');
        if (!productId || excluded.has(productId) || seen.has(productId)) continue;

        seen.add(productId);
        selected.push(product);
        if (selected.length >= limit) return selected;
      }
    }

    return selected;
  };

  const selectRecommendationResult = ({
    cartProductIds = [],
    complementaryGroups = [],
    relatedGroups = [],
    limit = 3,
  } = {}) => {
    const complementary = uniqueEligibleProducts(complementaryGroups, cartProductIds, limit);
    if (complementary.length > 0) {
      return { products: complementary, source: 'complementary' };
    }

    const related = uniqueEligibleProducts(relatedGroups, cartProductIds, limit);
    return { products: related, source: related.length > 0 ? 'related' : 'none' };
  };

  const selectRecommendationProducts = (options = {}) => {
    return selectRecommendationResult(options).products;
  };

  const getRecommendationHeading = (source, headings = {}) => {
    if (source === 'related') return headings.related || 'You might also like';
    return headings.complementary || 'Complete your uniform';
  };

  const isDirectAddProduct = (product) => {
    if (!Array.isArray(product?.variants) || product.variants.length !== 1) return false;
    return product.variants[0]?.available !== false;
  };

  const setRecommendationsLoading = (container, isLoading) => {
    if (!container) return null;

    container.hidden = false;
    container.setAttribute('aria-busy', String(Boolean(isLoading)));
    container
      .querySelector?.('[data-cart-recommendations-loader]')
      ?.setAttribute('aria-hidden', String(!isLoading));
    return container;
  };

  const beginRecommendationLoad = (grid, signature) => {
    const nextSignature = String(signature || '');
    if (!grid || !nextSignature) return false;
    if (grid.dataset.loadedSignature === nextSignature) return false;
    if (grid.dataset.loadingSignature === nextSignature) return false;

    grid.dataset.loadingSignature = nextSignature;
    return true;
  };

  const finishRecommendationLoad = (grid, signature, hasProducts) => {
    const completedSignature = String(signature || '');
    if (!grid || grid.dataset.loadingSignature !== completedSignature) return false;

    grid.dataset.loadingSignature = '';
    grid.dataset.loadedSignature = hasProducts ? completedSignature : '';
    return true;
  };

  const loadRecommendationResultWithRetry = async (loadResult, options = {}) => {
    const maxAttempts = Math.max(1, Number(options.maxAttempts) || 1);
    const delayMs = Math.max(0, Number(options.delayMs) || 0);
    const wait = options.wait || ((delay) => new Promise((resolve) => setTimeout(resolve, delay)));
    let result = { products: [], source: 'none' };

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      result = await loadResult(attempt);
      if (Array.isArray(result?.products) && result.products.length > 0) return result;
      if (attempt < maxAttempts) await wait(delayMs * attempt);
    }

    return result;
  };

  const buildDirectAddRequest = (variantId, sections = []) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{ id: String(variantId), quantity: 1 }],
      sections,
    }),
  });

  const calculateItemCount = (items = []) => {
    return (items || []).reduce((total, item) => total + Number(item?.quantity || 0), 0);
  };

  const setDirectAddLoading = (button, isLoading) => {
    if (!button) return null;
    button.disabled = Boolean(isLoading);
    button.setAttribute('aria-busy', String(Boolean(isLoading)));
    return button;
  };

  const updateCartCount = (itemCount, documentRef) => {
    if (!documentRef) return;
    documentRef.querySelector('[data-cart-count]')?.setAttribute('data-cart-count', String(itemCount));
    documentRef.querySelector('.layout--viewport')?.setAttribute('data-cart-empty', itemCount === 0);
  };

  const addVariantToCart = async (button, variantId, options = {}) => {
    const windowRef = options.windowRef || (typeof window !== 'undefined' ? window : undefined);
    const documentRef = options.documentRef === undefined ? windowRef?.document : options.documentRef;
    const fetchImpl = options.fetchImpl || windowRef?.fetch?.bind(windowRef);
    const routes = options.routes || {
      cartAdd: windowRef?.Shopify?.routes?.cart_add || '/cart/add',
      cart: windowRef?.Shopify?.routes?.cart || '/cart',
    };
    const jsonRoute = (route) => String(route || '').endsWith('.js') ? String(route) : `${route}.js`;

    if (!fetchImpl) throw new Error('Storefront cart requests are unavailable.');

    setDirectAddLoading(button, true);
    try {
      const request = buildDirectAddRequest(variantId, ['cart-drawer']);
      const response = await fetchImpl(jsonRoute(routes.cartAdd), request);
      if (!response.ok) throw new Error('Unable to add the recommended product.');

      const payload = await response.json();
      const cartResponse = await fetchImpl(jsonRoute(routes.cart), { credentials: 'same-origin' });
      if (!cartResponse.ok) throw new Error('Unable to refresh the cart.');

      const cartPayload = await cartResponse.json();
      const itemCount = calculateItemCount(cartPayload.items);
      const drawerSection = payload.sections?.['cart-drawer'];

      updateCartCount(itemCount, documentRef);
      windowRef?.trigger?.('theme:cart:productAdded', String(variantId));
      windowRef?.trigger?.('theme:cart:drawer:updateHtml', {
        section: drawerSection,
        load_transitions: true,
      });
      return { payload, cart: cartPayload };
    } finally {
      if (button?.isConnected) setDirectAddLoading(button, false);
    }
  };

  const syncRecommendationsRegion = (sectionHtml, documentRef) => {
    if (!sectionHtml || !documentRef) return null;

    const Parser = documentRef.defaultView?.DOMParser;
    if (!Parser) return null;

    const parsed = new Parser().parseFromString(String(sectionHtml), 'text/html');
    const incoming = parsed.querySelector('[data-cart-drawer-recommendations]');
    const currentSlot = documentRef.querySelector(
      ".drawer--container[data-view='cart-drawer'] .cart--drawer-recommendations-slot"
    );
    if (!currentSlot) return null;

    if (!incoming) {
      currentSlot.replaceChildren();
      return null;
    }

    const next = incoming.cloneNode(true);
    currentSlot.replaceChildren(next);
    return next;
  };

  return {
    addVariantToCart,
    beginRecommendationLoad,
    buildRecommendationsUrl,
    buildDirectAddRequest,
    finishRecommendationLoad,
    getRecommendationHeading,
    isDirectAddProduct,
    calculateItemCount,
    loadRecommendationResultWithRetry,
    parseProductIds,
    setRecommendationsLoading,
    setDirectAddLoading,
    selectRecommendationResult,
    selectRecommendationProducts,
    syncRecommendationsRegion,
    updateCartCount,
    uniqueEligibleProducts,
  };
});
